import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { MemberIndexEntry, MinistryMeta, QueryDoc } from '../types/types';
import { getMemberSummaries, getMinistries, getParties, getQuery } from '../data/cache';
import { photoFor } from '../utils/names';
import { shortPartyName } from '../utils/party';
import { color, font, space, text } from '../theme';
import DetailHeader from '../components/DetailHeader';
import { Card, Section } from '../components/Card';
import { Badge } from '../components/Badge';
import ListRow from '../components/ListRow';
import Avatar from '../components/Avatar';
import Button from '../components/Button';
import Icon from '../components/Icon';
import NavIcon from '../layout/NavIcon';
import { EmptyState, ErrorState } from '../components/States';
import { Skeleton, SkeletonList, SkeletonText } from '../components/Skeleton';
import PageContainer from '../layout/PageContainer';
import DetailHeaderFrame from '../layout/DetailHeaderFrame';

// Desktop reading column; the breadcrumb row above it (Layout width) uses the same width
const READING_WIDTH = 800;

const formatDate = (iso?: string | null): string | null =>
  iso ? new Date(iso).toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' }) : null;

const docKind = (url: string) => (url.endsWith('.pdf') ? 'PDF' : url.endsWith('.tif') ? 'תמונה סרוקה' : url.match(/\.docx?$/) ? 'Word' : 'מסמך');

const QueryDetailPage = () => {
  const { queryId } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState<QueryDoc | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'notFound' | 'error'>('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const [member, setMember] = useState<MemberIndexEntry | null>(null);
  const [partyName, setPartyName] = useState('');
  const [ministry, setMinistry] = useState<MinistryMeta | null>(null);

  useEffect(() => {
    if (!queryId) return;
    let cancelled = false;
    setStatus('loading');
    getQuery(queryId).then(q => {
      if (cancelled) return;
      if (!q) { setStatus('notFound'); return; }
      setQuery(q);
      setStatus('ready');

      if (q.memberId) {
        Promise.all([getMemberSummaries([q.memberId]), getParties()]).then(([members, parties]) => {
          if (cancelled) return;
          const m = members[q.memberId!] || null;
          setMember(m);
          const p = m?.partyId ? parties.find(pp => pp.id === m.partyId) : undefined;
          setPartyName(p ? shortPartyName(p.name || '') : '');
        }).catch(() => { /* ok — the MK card is optional */ });
      }
      if (q.govMinistryId) {
        getMinistries().then(byId => {
          if (!cancelled) setMinistry(byId[String(q.govMinistryId)] || null);
        }).catch(() => { /* ok — falls back to the stored name */ });
      }
    }).catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [queryId, reloadKey]);

  if (status === 'loading') {
    return (
      <Layout>
        <div data-skeleton="true" style={{ padding: space.lg, display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <Card><div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}><Skeleton width={120} height={20} /><SkeletonText lines={2} lineHeight={18} /></div></Card>
          <SkeletonList rows={2} avatar />
        </div>
      </Layout>
    );
  }
  if (status === 'error') {
    return <Layout><div style={{ padding: space.lg }}><Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card></div></Layout>;
  }
  if (status === 'notFound' || !query) {
    return (
      <Layout>
        <div style={{ padding: space.lg }}>
          <Card padding={0}>
            <EmptyState icon="document" title="השאילתה לא נמצאה" body="ייתכן שהקישור שגוי." action={<Button onClick={() => navigate('/queries', { replace: true })}>לכל השאילתות</Button>} />
          </Card>
        </div>
      </Layout>
    );
  }

  const q = query;
  const answered = !!q.replyDate;
  const ministryName = ministry?.name || q.govMinistryName || '';
  const from = `שאילתה מס׳ ${q.number}`;
  const dates = [
    { label: 'הוגשה', value: formatDate(q.submitDate) },
    { label: 'מועד תשובה מתוכנן', value: formatDate(q.replyDatePlanned) },
    { label: 'נענתה בתאריך', value: answered ? formatDate(q.replyDate) : null },
  ].filter(r => r.value);

  return (
    <Layout width={READING_WIDTH} breadcrumbs={[{ label: from }]}>
      {/* Desktop: a short document page, so one centred reading column rather than two panes */}
      <PageContainer maxWidth={READING_WIDTH}>
      <DetailHeaderFrame>
      <DetailHeader fallback="/queries" fallbackLabel="שאילתות">
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: space.xs + 2, flexWrap: 'wrap' }}>
            <Badge tone={q.typeDesc === 'דחופה' ? 'warning' : 'info'}>{q.typeDesc || 'רגילה'}</Badge>
            <Badge tone={answered ? 'success' : 'neutral'}>{answered ? 'נענתה' : 'ממתינה לתשובה'}</Badge>
            <span style={{ fontSize: font.small, color: color.textSecondary, marginInlineStart: 'auto' }}>שאילתה מס׳ {q.number}</span>
          </span>
          <h1 style={{ ...text.entityTitle, fontSize: 21, lineHeight: 1.35 }}>{q.name}</h1>
        </div>
      </DetailHeader>
      </DetailHeaderFrame>

      <div style={{ padding: space.lg, display: 'flex', flexDirection: 'column', gap: space.lg }}>
        <Card padding={0}>
          {q.memberId && (
            <ListRow
              divider={false}
              to={`/members/${q.memberId}`}
              state={{ from }}
              leading={<Avatar name={member?.name || '?'} src={member ? photoFor(member, 44) : undefined} size={44} />}
              title={member?.name || '…'}
              subtitle={['מגיש/ת השאילתה', partyName].filter(Boolean).join(' · ')}
            />
          )}
          <ListRow
            divider={Boolean(q.memberId)}
            to={q.govMinistryId && ministryName ? `/government/ministries/${q.govMinistryId}` : undefined}
            state={{ from }}
            leading={<span style={{ width: 44, height: 44, borderRadius: '50%', background: color.primarySoft, color: color.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><NavIcon name="government" size={20} /></span>}
            title={ministryName || 'לא צוין משרד'}
            subtitle="מופנית אל"
          />
        </Card>

        {dates.length > 0 && (
          <Card padding={0}>
            {dates.map((row, i) => (
              <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.md, minHeight: 48, padding: `0 ${space.lg}px`, borderTop: i > 0 ? `1px solid ${color.divider}` : 'none', fontSize: font.label }}>
                <span style={{ color: color.textSecondary }}>{row.label}</span>
                <span style={{ fontWeight: 500 }}>{row.value}</span>
              </div>
            ))}
          </Card>
        )}

        <Section title="מסמכים">
          {q.queryDocUrl || q.replyDocUrl
            ? (
              <Card padding={0}>
                {q.queryDocUrl && (
                  <ListRow divider={false} href={q.queryDocUrl} title="מסמך השאילתה" subtitle={docKind(q.queryDocUrl)}
                    leading={<span style={{ color: color.primary }}><Icon name="document" size={22} /></span>} />
                )}
                {q.replyDocUrl && (
                  <ListRow divider={Boolean(q.queryDocUrl)} href={q.replyDocUrl} title="תשובת השר/ה" subtitle={docKind(q.replyDocUrl)}
                    leading={<span style={{ color: '#27500A' }}><Icon name="document" size={22} /></span>} />
                )}
              </Card>
            )
            : <Card><p style={{ margin: 0, fontSize: font.small, color: color.textSecondary }}>אין מסמכים זמינים לשאילתה זו.</p></Card>}
        </Section>
      </div>
      </PageContainer>
    </Layout>
  );
};

export default QueryDetailPage;
